// Default phone details (India)
const defaultPhoneDetails = {
  number: '1800 210 1999',
  flag: '🇮🇳'
};

// Cache duration in milliseconds (24 hours)
const CACHE_DURATION = 24 * 60 * 60 * 1000;

// Function to get country-specific phone details
export const getCountryPhoneDetails = async () => {
  try {
    // Check cache first
    const cachedData = localStorage.getItem('countryPhoneDetails');
    if (cachedData) {
      const { data, timestamp } = JSON.parse(cachedData);
      
      // Check if cache is still valid (within 24 hours)
      if (Date.now() - timestamp < CACHE_DURATION) {
        return data;
      }
      // Cache expired, remove it
      localStorage.removeItem('countryPhoneDetails');
    }

    // If no valid cache, fetch from API
    const response = await fetch('https://ipapi.co/json/');
    const locationData = await response.json();
    
    // Default to India
    let phoneDetails = { ...defaultPhoneDetails };

    // Check country code
    if (locationData.country === 'GB') {
      phoneDetails = {
        number: '020 34113999',
        flag: '🇬🇧'
      };
    }

    // Cache the result
    localStorage.setItem('countryPhoneDetails', JSON.stringify({
      data: phoneDetails,
      timestamp: Date.now()
    }));

    return phoneDetails;
  } catch (error) {
    console.error('Error detecting country:', error);
    
    // Try to use cached data even if expired in case of error
    const cachedData = localStorage.getItem('countryPhoneDetails');
    if (cachedData) {
      const { data } = JSON.parse(cachedData);
      return data;
    }
    
    // Fallback to India if there's an error and no cache
    return defaultPhoneDetails;
  }
};

// Convert ISO2 country code to flag emoji
const iso2ToFlagEmoji = (iso2) => {
  try {
    if (!iso2 || typeof iso2 !== 'string') return '';
    const codePoints = iso2
      .toUpperCase()
      .split('')
      .map((char) => 127397 + char.charCodeAt(0));
    return String.fromCodePoint(...codePoints);
  } catch (e) {
    return '';
  }
};

// Cache keys and durations
const GEO_CACHE_KEY = 'visitorCountryInfo';
const GEO_CACHE_DURATION = 24 * 60 * 60 * 1000; // 24h
const CODES_CACHE_KEY = 'countryCallingCodes';
const CODES_CACHE_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 days

// Fetch visitor country info from IP (ISO2, country name, calling code, flag)
export const getVisitorCountryInfo = async () => {
  try {
    const cached = localStorage.getItem(GEO_CACHE_KEY);
    if (cached) {
      const { data, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp < GEO_CACHE_DURATION) {
        return data;
      }
      localStorage.removeItem(GEO_CACHE_KEY);
    }

    const response = await fetch('https://ipapi.co/json/');
    const json = await response.json();
    const iso2 = json?.country || 'IN';
    const callingCode = json?.country_calling_code || '+91';
    const countryName = json?.country_name || 'India';
    const flagEmoji = iso2ToFlagEmoji(iso2);

    const data = { iso2, countryName, callingCode, flagEmoji };

    localStorage.setItem(GEO_CACHE_KEY, JSON.stringify({ data, timestamp: Date.now() }));
    return data;
  } catch (error) {
    console.error('Error fetching visitor country info:', error);
    // Fallback to India
    return { iso2: 'IN', countryName: 'India', callingCode: '+91', flagEmoji: '🇮🇳' };
  }
};

// Fetch and cache all country calling codes for dropdown
export const getAllCountryCallingCodes = async () => {
  try {
    const cached = localStorage.getItem(CODES_CACHE_KEY);
    if (cached) {
      const { data, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp < CODES_CACHE_DURATION) {
        return data;
      }
      localStorage.removeItem(CODES_CACHE_KEY);
    }

    const resp = await fetch('https://restcountries.com/v3.1/all?fields=name,cca2,idd');
    const countries = await resp.json();
    const options = countries
      .map((c) => {
        const iso2 = c?.cca2;
        const name = c?.name?.common;
        const root = c?.idd?.root || '';
        const suffixes = Array.isArray(c?.idd?.suffixes) ? c.idd.suffixes : [];
        const firstCode = root && suffixes.length > 0 ? `${root}${suffixes[0]}` : (root || '');
        if (!iso2 || !name || !firstCode) return null;
        return {
          iso2,
          name,
          code: firstCode.startsWith('+') ? firstCode : `+${firstCode}`,
          flagEmoji: iso2ToFlagEmoji(iso2)
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.name.localeCompare(b.name));

    localStorage.setItem(CODES_CACHE_KEY, JSON.stringify({ data: options, timestamp: Date.now() }));
    return options;
  } catch (error) {
    console.error('Error fetching country calling codes:', error);
    // Minimal fallback
    return [
      { iso2: 'IN', name: 'India', code: '+91', flagEmoji: '🇮🇳' },
      { iso2: 'GB', name: 'United Kingdom', code: '+44', flagEmoji: '🇬🇧' },
      { iso2: 'US', name: 'United States', code: '+1', flagEmoji: '🇺🇸' }
    ];
  }
};
